/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchBtn,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ContentSection } from 'components/layouts/ContentSection';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import GatingResultReportGrid from './gatingresultreport/GatingResultReportGrid';
import { SearchParam } from './GatingRegistPage';
import { getYearList } from 'apis/gtng/GatingRegist';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { ComboBox } from 'components/selects/ComboBox';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Gating 결과 Report Library Page
 * @param
 * @constructor
 */
const GatingResultReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParam, setSearchParam] = useState<SearchParam>({});
  const [searchParamData, setSearchParamData] = useState<SearchParam>();
  const [searchState, setSearchState] = useState<any>(false);
  const [gatingTypeConditionList, setGatingTypeConditionList] = useState<Code[]>([]);
  const [gatingStepCodeConditionList, setGatingStepCodeConditionList] = useState<Code[]>([]);
  const [gatingProductConditionList, setGatingProductConditionList] = useState<Code[]>([]);
  const [gatingStartEndYearList, setGatingStartEndYearList] = useState<any>();
  const location = useLocation();
  const optionsGatingStartEndYearList = useMemo(() => {
    return (gatingStartEndYearList || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: String(cur.STARTYEAR),
          cmnCdNm: String(cur.STARTYEAR),
        },
      ],
      [] as Code[]
    );
  }, [gatingStartEndYearList]);

  const state = { ...location.state };

  useEffect(() => {
    initCondition();
    btnSerch();
  }, []);

  useEffect(() => {
    if (state.sResultReprotParam != undefined) {
      setSearchParam(state.sResultReprotParam);
      setSearchParamData(state.sResultReprotParam);
    }
  }, [state.sResultReprotParam]);

  const handleGatingTypeCode = (value) => {
    setSearchParam({
      ...searchParam,
      gatingTypeCode: value,
    });

    getGatingStepCode(value);
  };

  const getGatingStepCode = async (value) => {
    if (value == '') {
      setGatingStepCodeConditionList([]);
    } else {
      const gatingStepCode = await getCommonCodeNamesCondition({
        optValCtn1: value,
        cmnGrCd: 'GATING_STEP_CODE',
      });
      setGatingStepCodeConditionList(gatingStepCode);
    }
  };

  //콤보박스 공통코드 호출
  const initCondition = async () => {
    //Gating 유형
    const gatingTypeCodeConditions = await getCommonCodeNames('GATING_TYPE_CODE');
    setGatingTypeConditionList(gatingTypeCodeConditions);
    //Gating 단계
    const gatingStepCodeConditions = await getCommonCodeNames('GATING_STEP_CODE');
    setGatingStepCodeConditionList(gatingStepCodeConditions);
    //Product Type
    const gatingProductTypeConditions = await getCommonCodeNames('GATING_PRODUCT_TYPE_CODE');
    setGatingProductConditionList(gatingProductTypeConditions);
    const gatingStartEndYearConditions = await getYearList();
    setGatingStartEndYearList(gatingStartEndYearConditions);
  };

  const handleSearchParam = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const btnReload = () => {
    setSearchParam(
      (prev) =>
        Object.keys(prev).reduce((acc, cur) => Object.assign(acc, { [cur]: '' }), {}) as SearchParam
    );
  };

  const btnSerch = () => {
    setSearchParamData(searchParam);
    if (searchState == true) {
      setSearchState(false);
    }
    if (searchState == false) {
      setSearchState(true);
    }
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('gtng.label.실시년도', '실시년도')}</label>
                <ComboBox
                  options={optionsGatingStartEndYearList}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam.startDate}
                  onChange={(value) =>
                    setSearchParam({
                      ...searchParam,
                      startDate: value,
                    })
                  }
                />
              </SearchCols>
              <SearchCols>
                <label>{t('gtng.label.Gating 유형', 'Gating 유형')}</label>
                <ComboBox
                  options={gatingTypeConditionList}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam.gatingTypeCode}
                  onChange={handleGatingTypeCode}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('gtng.label.Gating 단계', 'Gating 단계')}</label>
                <ComboBox
                  options={gatingStepCodeConditionList}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam.gatingStepCode}
                  onChange={(value) =>
                    setSearchParam({
                      ...searchParam,
                      gatingStepCode: value,
                    })
                  }
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('gtng.label.Gating Task명', 'Gating Task명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="gatingName"
                  placeholder={String(
                    t('gtng.msg.Gating Task명을 입력해 주세요.', 'Gating Task명을 입력해 주세요.')
                  )}
                  value={searchParam.gatingName}
                  onChange={handleSearchParam}
                ></CustomInputWithSearch>
              </SearchCols>
              <SearchCols>
                <label>{t('gtng.label.Product Type', 'Product Type')}</label>
                <ComboBox
                  options={gatingProductConditionList}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam.productTypeCode}
                  onChange={(value) =>
                    setSearchParam({
                      ...searchParam,
                      productTypeCode: value,
                    })
                  }
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button
              css={IconButton.button}
              className="reload"
              onClick={btnReload}
              disableRipple
            ></Button>
            <Button css={IconButton.button} className="find" onClick={btnSerch} disableRipple>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      {/*<ContentSection>*/}
      <GatingResultReportGrid
        editable={true}
        searchParamData={searchParamData}
        searchState={searchState}
      />
      {/*</ContentSection>*/}
    </>
  );
};

export default GatingResultReportPage;
